import {DispatchRequestEntity, transportCategory, useGetDispatchRequestTrackingQuery} from "@ots-frontend/ots-data";
import {Card, Divider, Grid, Group, Text, Title} from "@mantine/core";
import {useTranslation} from "react-i18next";
import {DefaultText} from "../../text/DefaultText/DefaultText";
import {DispatchRequestTimeline} from "../../timeline/DispatchRequestTimeline/DispatchRequestTimeline";
import {FormattedDateTime} from "../../date/FormattedDateTime/FormattedDateTime";
import { EntityAnchor } from "../../anchor/EntityAnchor/EntityAnchor";
import {DispatchTrackingCard} from "../common/DispatchTrackingCard/DispatchTrackingCard";
import {
  getDispatchTrackingPollingInterval,
  isDispatchTrackingClosedStatus
} from "../common/DispatchTrackingCard/dispatch-tracking-polling";
import {useEffect, useState} from "react";

export type DispatchRequestDetailsProps = {
  dispatchRequest: DispatchRequestEntity
}

export const DispatchRequestDetails = (props: DispatchRequestDetailsProps) => {
  const {dispatchRequest} = props;

  const {t} = useTranslation(["common"]);

  const [trackingPollingInterval, setTrackingPollingInterval] = useState(() =>
    getDispatchTrackingPollingInterval(dispatchRequest.status)
  );
  const trackingClosed = isDispatchTrackingClosedStatus(dispatchRequest.status);

  const {
    data: tracking,
    isLoading: isTrackingLoading,
    isFetching: isTrackingFetching,
    error: trackingError,
    refetch: refetchTracking
  } = useGetDispatchRequestTrackingQuery(dispatchRequest.id, {
    pollingInterval: trackingPollingInterval,
    skipPollingIfUnfocused: true,
    skip: trackingClosed
  });

  useEffect(() => {
    setTrackingPollingInterval(getDispatchTrackingPollingInterval(dispatchRequest.status, tracking));
  }, [dispatchRequest.status, tracking]);

  return (
    <Grid>
      <Grid.Col span={6}>
        <Card shadow="sm" padding="lg" radius="md" withBorder>

          <Group position="apart" pb="sm">
            <Title order={4}>{t("profile.dispatchrequest.info.section.details.label", "Details")}</Title>
          </Group>

          <Divider/>

          <Grid>
            <Grid.Col span={6}>
              <Text ta="left" fz="sm" weight={500} mt="md">
                {t("profile.dispatchrequest.info.field.id.label", "ID")}
              </Text>
              <Text ta="left" c="dimmed" fz="xs">
                <DefaultText value={dispatchRequest.resourceId}/>
              </Text>
            </Grid.Col>
            <Grid.Col span={6}>
              <Text ta="left" fz="sm" weight={500} mt="md">
                {t("profile.dispatchrequest.info.field.workordernumber.label", "Work Order #")}
              </Text>
              <Text ta="left" c="dimmed" fz="xs">
                {dispatchRequest.workOrderNumber}
              </Text>
            </Grid.Col>
            <Grid.Col span={12}>
              <Text ta="left" fz="sm" weight={500} mt="md">
                {t("profile.dispatchrequest.info.field.externalid.label", "External ID")}
              </Text>
              <Text ta="left" c="dimmed" fz="xs">
                {dispatchRequest.externalId}
              </Text>
            </Grid.Col>
            <Grid.Col span={6}>
              <Text ta="left" fz="sm" weight={500} mt="md">
                {t("profile.dispatchrequest.info.field.requestedby.label", "Requested By")}
              </Text>
              <Text ta="left" c="dimmed" fz="xs">
                <DefaultText value={dispatchRequest.createdBy}/>
              </Text>
            </Grid.Col>
            <Grid.Col span={6}>
              <Text ta="left" fz="sm" weight={500} mt="md">
                {t("profile.dispatchrequest.info.field.authorizedby.label", "Authorized By")}
              </Text>
              <Text ta="left" c="dimmed" fz="xs">
                {dispatchRequest.isAuthorized ?
                  <DefaultText value={dispatchRequest.authorizedBy}/> : undefined}
              </Text>
            </Grid.Col>
            <Grid.Col span={12}>
              <Divider variant={"dashed"}/>
            </Grid.Col>
            <Grid.Col span={6}>
              <Text ta="left" fz="sm" weight={500} mt="md">
                {t("profile.dispatchrequest.info.field.scheduleddate.label", "Scheduled Date")}
              </Text>
              <Text ta="left" c="dimmed" fz="xs">
                <FormattedDateTime date={dispatchRequest.scheduledDttm}/>
              </Text>
            </Grid.Col>
            <Grid.Col span={6}>
              <Text ta="left" fz="sm" weight={500} mt="md">
                {t("profile.dispatchrequest.info.field.scheduledenddate.label", "Completion Deadline")}
              </Text>
              <Text ta="left" c="dimmed" fz="xs">
                <FormattedDateTime date={dispatchRequest.scheduledEndDttm}/>
              </Text>
            </Grid.Col>
            <Grid.Col span={6}>
              <Text ta="left" fz="sm" weight={500} mt="md">
                {t("profile.dispatchrequest.info.field.accepttimelimit.label", "Accept Time Limit")}
              </Text>
              <Text ta="left" c="dimmed" fz="xs">
                <DefaultText value={dispatchRequest.acceptTimeLimit} defaultValue={t<string>("common.notapplicable")}/>
              </Text>
            </Grid.Col>
            <Grid.Col span={12}>
              <Divider variant={"dashed"}/>
            </Grid.Col>
            <Grid.Col span={12}>
              <Text ta="left" fz="sm" weight={500} mt="md">
                {t("profile.dispatchrequest.info.field.truckcompany.label", "Truck Company")}
              </Text>
              <Text ta="left" c="dimmed" fz="xs">
                <DefaultText value={dispatchRequest.truckingCompanyName}/>
              </Text>
            </Grid.Col>
            <Grid.Col span={12}>
              <Divider variant={"dashed"}/>
            </Grid.Col>
            <Grid.Col span={6}>
              <Text ta="left" fz="sm" weight={500} mt="md">
                {t("profile.dispatchrequest.info.field.quantity.label", "Quantity")}
              </Text>
              <Text ta="left" c="dimmed" fz="xs">
                {dispatchRequest.quantityDisplay}
              </Text>
            </Grid.Col>
            <Grid.Col span={6}>
              <Text ta="left" fz="sm" weight={500} mt="md">
                {t("profile.dispatchrequest.info.field.freighttype.label", "Freight Type")}
              </Text>
              <Text ta="left" c="dimmed" fz="xs">
                <DefaultText value={dispatchRequest.freightTypeName}/>
              </Text>
            </Grid.Col>
            {(dispatchRequest.category === transportCategory.water || dispatchRequest.serviceTypeId) &&
              <Grid.Col span={12}>
                <Text ta="left" fz="sm" weight={500} mt="md">
                  {t("profile.dispatchrequest.info.field.servicetype.label", "Service Type")}
                </Text>
                <Text ta="left" c="dimmed" fz="xs">
                  {dispatchRequest.serviceTypeId ?
                    <DefaultText value={dispatchRequest.serviceTypeName}/> : undefined}
                </Text>
              </Grid.Col>
            }
            <Grid.Col span={12}>
              <Divider variant={"dashed"}/>
            </Grid.Col>
            <Grid.Col span={12}>
              <Grid>
                <Grid.Col span={6}>
                  <Text ta="left" fz="sm" weight={500} mt="md">
                    {t("profile.dispatchrequest.info.field.pickupsite.label", "Pickup Site")}
                  </Text>
                  <Text ta="left" c="dimmed" fz="xs">
                    <EntityAnchor
                      value={{
                        objectId: dispatchRequest.pickupSiteId,
                        objectName: dispatchRequest.pickupSiteName ?? t<string>("common.shared.unspecified", "Unspecified"),
                        objectType: 'SITE'
                      }}
                    />
                  </Text>
                  <Text ta="left" fz="sm" weight={500} mt="md">
                    {t("profile.dispatchrequest.info.field.pickupwell.label", "Pickup Well")}
                  </Text>
                  <Text ta="left" c="dimmed" fz="xs">
                    <EntityAnchor
                      value={{
                        objectId: dispatchRequest.pickupWellId,
                        objectName: dispatchRequest.pickupWellName ?? t<string>("common.shared.unspecified", "Unspecified"),
                        objectType: 'WELL'
                      }}
                    />
                  </Text>
                  <Text ta="left" fz="sm" weight={500} mt="md">
                    {t("profile.dispatchrequest.info.field.pickupcontainer.label", "Pickup Container")}
                  </Text>
                  <Text ta="left" c="dimmed" fz="xs">
                    <EntityAnchor
                      value={{
                        objectId: dispatchRequest.pickupContainerId,
                        objectName: dispatchRequest.pickupContainerName ?? t<string>("common.shared.unspecified", "Unspecified"),
                        objectType: 'CONTAINER'
                      }}
                    />
                  </Text>
                </Grid.Col>
                <Grid.Col span={6}>
                  <Text ta="left" fz="sm" weight={500} mt="md">
                    {t("profile.dispatchrequest.info.field.deliverysite.label", "Delivery Site")}
                  </Text>
                  <Text ta="left" c="dimmed" fz="xs">
                    <EntityAnchor
                      value={{
                        objectId: dispatchRequest.deliverySiteId,
                        objectName: dispatchRequest.deliverySiteName ?? t<string>("common.shared.unspecified", "Unspecified"),
                        objectType: 'SITE'
                      }}
                    />
                  </Text>
                  <Text ta="left" fz="sm" weight={500} mt="md">
                    {t("profile.dispatchrequest.info.field.deliverywell.label", "Delivery Well")}
                  </Text>
                  <Text ta="left" c="dimmed" fz="xs">
                    <EntityAnchor
                      value={{
                        objectId: dispatchRequest.deliveryWellId,
                        objectName: dispatchRequest.deliveryWellName ?? t<string>("common.shared.unspecified", "Unspecified"),
                        objectType: 'WELL'
                      }}
                    />
                  </Text>
                  <Text ta="left" fz="sm" weight={500} mt="md">
                    {t("profile.dispatchrequest.info.field.deliverycontainer.label", "Delivery Container")}
                  </Text>
                  <Text ta="left" c="dimmed" fz="xs">
                    <EntityAnchor
                      value={{
                        objectId: dispatchRequest.deliveryContainerId,
                        objectName: dispatchRequest.deliveryContainerName ?? t<string>("common.shared.unspecified", "Unspecified"),
                        objectType: 'CONTAINER'
                      }}
                    />
                  </Text>
                </Grid.Col>
              </Grid>
            </Grid.Col>
          </Grid>
        </Card>
      </Grid.Col>

      <Grid.Col span={6}>
        <Card shadow="sm" padding="lg" radius="md" withBorder>
          <Group position="apart" pb="sm">
            <Title order={4}>{t("profile.dispatchrequest.info.section.timeline.label", "Timeline")}</Title>
          </Group>
          <Divider/>
          <Grid>
            <Grid.Col span={12}>
              <DispatchRequestTimeline
                m="md"
                bulletSize={24}
                lineWidth={2}
                dispatchRequest={dispatchRequest}/>
            </Grid.Col>
          </Grid>
        </Card>
      </Grid.Col>

      {!trackingClosed &&
        <Grid.Col span={12}>
          <DispatchTrackingCard
            tracking={tracking}
            loading={isTrackingLoading}
            fetching={isTrackingFetching}
            error={trackingError}
            refreshInterval={trackingPollingInterval}
            onRefresh={refetchTracking}/>
        </Grid.Col>
      }

    </Grid>
  );
};

